import { ErrorOrigin, logWarn, type LogTarget, ValidationError } from '@amazon/vinyl-util'
import { GQL_NETWORK_FIRST, type SanitizedResult } from '@amzn/bf-gql'
import { createAuthScopedMemo } from '../env/authScopedMemo'
import type { PlaybackAuth, PlaybackGqlClient, SessionContext } from '../env/PlaybackEnv'
import { gql } from '../gql/gql'
import { tierEntitlements } from './tierEntitlements'

// One per-session read of the signed-in user's tier, entitled audio qualities and
// preferences, so boot and sign-in fetch them in a single round trip.
export const userContextQuery = gql(`
    query UserContext {
        user {
            id
            tier
            playableAudioQualities
            market {
                id
            }
        }
        userPreferences {
            isExplicitLanguageAllowed
        }
    }
`)

export type UserContextResult = SanitizedResult<typeof userContextQuery>

export type UserTier = NonNullable<UserContextResult['user']>['tier']

export interface UserContextDeps {
    readonly auth: Pick<PlaybackAuth, 'status$'>
    readonly gql: PlaybackGqlClient
    /** The signed-in customer's id, null when the session has none. */
    readonly loadCustomerId: () => Promise<string | null>
}

const target: LogTarget = { logPrefix: 'userContext' }

/** The account reads of a {@link SessionContext}, over one memoized user-context query. */
export interface UserContextSession extends Pick<
    SessionContext,
    'getCustomer' | 'getPlayableAudioQualities'
> {
    /** Rejects when signed out: the gateway denies the per-user query to an anonymous session. */
    loadUserContext(): Promise<UserContextResult>
}

/**
 * Memoized per auth status, so a sign-out or sign-in re-reads the new account's
 * tier, qualities and preferences. Each read goes to the network and writes the
 * gql cache, so the next boot can read the account's market before it lands.
 */
export function createUserContextSession({
    auth,
    gql,
    loadCustomerId,
}: UserContextDeps): UserContextSession {
    const authScope = () => ({ status: auth.status$ })
    const memo = createAuthScopedMemo(authScope, () =>
        gql.fetch(
            userContextQuery,
            {},
            { useCache: true, cacheControlOverrides: GQL_NETWORK_FIRST }
        )
    )
    const customerId = createAuthScopedMemo(authScope, loadCustomerId)
    // A failed id read sends no id rather than failing playback, and is retried on the next read.
    const readCustomerId = () =>
        customerId.get().catch((err: unknown) => {
            logWarn(target, 'customer id unavailable', err)
            return null
        })
    return {
        loadUserContext: memo.get,
        async getCustomer() {
            const [{ user }, id] = await Promise.all([memo.get(), readCustomerId()])
            // An entitlement-free customer would look like a real unentitled account to DMLS.
            if (!user?.id)
                throw new ValidationError('user not returned', ErrorOrigin.SERVICE_EXTERNAL)
            return { customerId: id, entitlementList: tierEntitlements(user.tier) }
        },
        getPlayableAudioQualities: async () =>
            (await memo.get()).user?.playableAudioQualities ?? [],
    }
}
